import { useCallback, useEffect, useRef, useState } from "react";
import { Api } from "../api";
import { SITE_DATA_PARTITIONS } from "../../../types";
import { UserArtLikeData } from "../../../types/UserArtLike";

export type RequestStatus =
  | "idle"
  | "server-error"
  | "present"
  | "no-slug"
  | "request-error"
  | "loading";

export type Request = {
  status: RequestStatus;
  items?: UserArtLikeData[];
  slug?: string;
  error?: any;
};

export const useLikeArt = (props: { slug?: string; userId: string }) => {
  const shouldRequest = useRef(!!props?.slug);

  useEffect(() => {
    if (props?.slug) {
      shouldRequest.current = true;
    }
  }, [props?.slug]);
  const [request, setRequest] = useState<Request>({
    status: shouldRequest.current ? "loading" : "no-slug",
  });

  const [likeRequest, setLikeRequest] = useState<{
    status: "no-op" | "success" | "error" | "loading";
    error?: any;
  }>({
    status: "no-op",
  });
  const requestData = useCallback(() => {
    if (props?.slug) {
      setRequest({
        status:
          // props?.slug ? "loading" :
          "no-slug",
      });

      Api.getRelated<UserArtLikeData>({
        type: SITE_DATA_PARTITIONS.ART,
        slug: props.slug,
        subType: SITE_DATA_PARTITIONS.USER_ART_LIKE,
      })
        .then((resp) =>
          resp.success
            ? setRequest((req) => ({
                status: "present" as const,
                items: resp.body.items,
                slug: props.slug,
              }))
            : setRequest((req) => ({
                status: "server-error" as const,
                error: resp,
                slug: props.slug,
                oldReq: req,
              })),
        )
        .catch((e) =>
          setRequest((req) => ({
            status: "request-error" as const,
            error: e,
            slug: props.slug,
          })),
        );
    }
  }, [props?.slug, setRequest]);

  const like = useCallback(() => {
    if (props?.slug) {
      Api.create({
        type: SITE_DATA_PARTITIONS.USER_ART_LIKE,
        slug: props?.userId,
        artSlug: props?.slug,
      } as UserArtLikeData)
        .then((resp) =>
          resp.success
            ? setLikeRequest(() => ({
                status: "success" as const,
              }))
            : setLikeRequest((req) => ({
                status: "error",
                error: resp,
              })),
        )
        .catch((e) => setLikeRequest({ status: "error", error: e }))
        .then(() => requestData());
    }
  }, [setLikeRequest]);
  const unLikeArt = useCallback(() => {
    if (props?.slug) {
      Api.delete({
        type: SITE_DATA_PARTITIONS.USER_ART_LIKE,
        slug: props.userId,
        artSlug: props.slug,
      } as UserArtLikeData)
        .then((resp) =>
          resp.success
            ? setLikeRequest(() => ({
                status: "success" as const,
              }))
            : setLikeRequest((req) => ({
                status: "error",
                error: resp,
              })),
        )
        .catch((e) => setLikeRequest({ status: "error", error: e }))
        .then(() => requestData());
    }
  }, [setLikeRequest]);
  useEffect(() => {
    if (shouldRequest.current && props?.slug) {
      shouldRequest.current = false;
      requestData();
    }
  }, [shouldRequest, request, props?.slug]);
  return {
    request,
    likes: request?.items,
    likeArt: like,
    unLikeArt,
    liked:
      (request?.items?.findIndex((item) => item.slug === props.userId) ?? -1) >
      -1,
    likeRequest,
  };
};
